import { FormControl, SelectChangeEvent, SelectOption, SimpleSelect, Tooltip } from '@chronosphereio/chrono-ui';
import { getFormattedDateTimeRange } from '@chronosphereio/core';
import { sub } from 'date-fns';
import { ReactNode, useCallback } from 'react';
import { useTimeRange } from '@/plugin-system/runtime/TimeRangeProviderV2';
import { useUserTimeFormat } from '@/plugin-system/runtime/useUserTimeFormat';
import { CompareModeType, PrecedingPeriod, useCompareMode } from '../../plugin-system/runtime/compare-mode';
import { useTimeZone } from '../context/TimeZoneProvider';

const OffOption: SelectOption = { key: '', label: 'Off' };

export const CompareOptions: SelectOption[] = [
  OffOption,
  { key: PrecedingPeriod, label: 'Preceding period' },
  { key: '1d', label: '1 day prior' },
  { key: '7d', label: '7 days prior' },
  { key: '14d', label: '14 days prior' },
  { key: '30d', label: '30 days prior' },
];

const PrecedingPeriodTooltipWrapper = ({ children }: { children: ReactNode }) => {
  const { compareMode } = useCompareMode();
  const { timeZone } = useTimeZone();
  const timeFormat = useUserTimeFormat();

  const {
    absoluteTimeRange: { start, end },
  } = useTimeRange();

  if (compareMode !== null) {
    const duration = { seconds: (end.getTime() - start.getTime()) / 1000 };
    const compareTimeRange = getFormattedDateTimeRange({
      startDate: sub(start, duration).toString(),
      endDate: sub(end, duration).toString(),
      timeZone,
      timeFormat,
    });

    return (
      <Tooltip
        placement="top"
        Content={compareTimeRange}
        componentsProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  // shifting the tooltip so it displays above the input, otherwise it doesn't have enough room
                  offset: [0, -4],
                },
              },
            ],
          },
        }}
      >
        <span>{children}</span>
      </Tooltip>
    );
  } else {
    return <>{children}</>;
  }
};

export const CompareControls = () => {
  const { compareMode, setCompareMode } = useCompareMode();

  const handleChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const mode = event.target.value;
      if (mode === '') {
        setCompareMode(null);
      } else {
        setCompareMode(mode as CompareModeType);
      }
    },
    [setCompareMode]
  );

  return (
    <FormControl sx={{ minWidth: '100px' }}>
      <PrecedingPeriodTooltipWrapper>
        <SimpleSelect
          label="Compare"
          value={compareMode || OffOption.key}
          onChange={handleChange}
          options={CompareOptions}
        />
      </PrecedingPeriodTooltipWrapper>
    </FormControl>
  );
};
